/**
 * v4.38.0 — Experience-type badge.
 *
 * Single source of truth for the visual identity of a Review Shield
 * variant across:
 *   • Cards page
 *   • Card settings modal preview
 *   • Results portal (table row + filter labels)
 *   • Analytics chrome
 *   • Print + CSV export legends
 *
 * Operators should never need to guess which card type they are
 * looking at. Each variant gets an emoji + branded product name from
 * the locale file. Color tokens stay calm — this is identity, not
 * priority signal.
 */
import { useTranslation } from 'react-i18next';

const STYLE = {
    dine_in:  { ring: 'rgba(74, 163, 255, 0.45)', bg: 'rgba(74, 163, 255, 0.10)',  fg: '#4AA3FF' },
    pickup:   { ring: 'rgba(255, 179, 71, 0.50)', bg: 'rgba(255, 179, 71, 0.10)',  fg: '#F7A933' },
    delivery: { ring: 'rgba(48, 227, 140, 0.50)', bg: 'rgba(48, 227, 140, 0.10)',  fg: '#30E38C' },
};

const NORMALIZE = (kind) => (kind === 'pickup' || kind === 'delivery' || kind === 'dine_in') ? kind : 'dine_in';

export const ExperienceBadge = ({ kind, size = 'sm', showTagline = false, className = '' }) => {
    const { t } = useTranslation();
    const k = NORMALIZE(kind);
    const tokens = STYLE[k];
    const emoji = t(`reviewShield.experience.${k}_emoji`);
    const name = t(`reviewShield.experience.product.${k}.name`);
    const tagline = t(`reviewShield.experience.product.${k}.tagline`);

    const sizing = size === 'lg'
        ? { padX: 'px-3', padY: 'py-1.5', text: 'text-sm' }
        : size === 'xs'
            ? { padX: 'px-1.5', padY: 'py-0.5', text: 'text-[10px]' }
            : { padX: 'px-2', padY: 'py-1', text: 'text-xs' };

    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${sizing.padX} ${sizing.padY} ${sizing.text} ${className}`}
            style={{ background: tokens.bg, borderColor: tokens.ring, color: tokens.fg }}
            title={showTagline ? tagline : undefined}
            data-testid={`experience-badge-${k}`}
            data-insight-id={`experience-badge-${k}`}
            data-experience-kind={k}
        >
            <span aria-hidden="true">{emoji}</span>
            <span>{name}</span>
        </span>
    );
};

export const ExperienceLabel = ({ kind }) => {
    const { t } = useTranslation();
    const k = NORMALIZE(kind);
    const emoji = t(`reviewShield.experience.${k}_emoji`);
    const short = t(`reviewShield.experience.${k}`);
    return <span><span aria-hidden="true">{emoji}</span> {short}</span>;
};

export default ExperienceBadge;
